import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "@/context/AuthContext";
import EstimatingApp from "@/estimating/EstimatingApp";

/**
 * OLD estimating tab — hosts the ported Steeltooth estimating subsystem
 * (projects → blueprints → takeoff → AI analyse/estimate → quotes →
 * schedules → plugins), fully contained under /api/estimating with its
 * own est_* collections.
 *
 * The module renders in its OWN React root because it carries its own
 * MemoryRouter and React Router forbids nesting a <Router> inside the
 * host app's <BrowserRouter>. A second root also gives the port true
 * "app within an app" isolation. AuthContext is router-free, so it is
 * provided again here (reads the same cached session).
 *
 * `.estimating-port` scopes the CSS overrides in index.css that clamp the
 * module's full-viewport (h-dvh) page roots to the dashboard tab area.
 */
const EstimateTab = () => {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // StrictMode mounts → cleans up → re-mounts in dev. The cleanup defers
    // unmount by a tick; if the effect re-runs first, cancel the pending
    // unmount and keep the existing root (never createRoot twice on one node).
    if (host._estUnmountTimer) {
      clearTimeout(host._estUnmountTimer);
      host._estUnmountTimer = null;
      return;
    }
    if (!host._estRoot) {
      const root = createRoot(host);
      host._estRoot = root;
      root.render(
        <AuthProvider>
          <EstimatingApp />
        </AuthProvider>,
      );
    }
    return () => {
      host._estUnmountTimer = setTimeout(() => {
        host._estUnmountTimer = null;
        if (host._estRoot) {
          host._estRoot.unmount();
          host._estRoot = null;
        }
      }, 0);
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className="estimating-port h-full min-h-0"
      data-testid="estimate-tab"
    />
  );
};

export default EstimateTab;
